
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Alert Context
********************************************************************************************
Boilerplate context, consumer, provider and hook

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       2nd April 2022

*******************************************************************************************/
import React                    from 'react';
import moment                   from 'moment';
import { AlertSnackbar }        from 'components';

// The Product Context 
const AlertContext = React.createContext(undefined);

// const EMPTY_MESSAGE = {severity : undefined, message: undefined};

const CHECK_INVERVAL    = 500;  // 500ms
const MAX_AGE           = 2500; // 2s

// Alert Provider
const AlertProvider = ({children, snackbar = true, checkInterval = CHECK_INVERVAL, maxAge = MAX_AGE} = {}) => {

    // Hold Message Array
    const [messages,    setMessages]    = React.useState([]);

    const [quantity,firstMessage] = React.useMemo(() => {
        const quantity = messages.length;
        return [quantity, quantity > 0 ? messages[0] : undefined]
    }, [messages]);
    
    // Perform New Alert
    const alert = React.useCallback((message, severity = 'info') => {
        setMessages(prev => {
            const next = { id: Date.now(), date: moment(), message, severity };
            const last = prev[prev.length - 1];

            // De-Dupe
            if (last && last.message === message && last.severity === severity) 
                return prev;

            // New Message
            return [...prev, next];
        });
    }, []);

    // Clear Messages
    const clear = React.useCallback(() => setMessages([]),[])

    // Close and Remove the first message
    const handleClose = React.useCallback( () => setMessages(prev => prev.slice(1)), []);

    // Remove
    React.useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            setMessages(prev =>
                prev.length > 0 
                    ? prev.filter(msg => !msg.id || now - msg.id < maxAge)
                    : prev
            );
        }, checkInterval); // Check every 500ms or so
    
        return () => {
            clearInterval(interval);
        }
    }, [maxAge, checkInterval]);

    // Context values
    const value = {
        checkInterval,
        maxAge,
        messages,
        quantity,
        firstMessage, 
        clear,
        handleClose,
        alert
    };

    return (
        <AlertContext.Provider value={value}>
            {children}
            {
                snackbar && 
                <AlertSnackbar />
            }
        </AlertContext.Provider>
    )
}

// Alert Consumer
const AlertConsumer = ({children}) => {
    return (
        <AlertContext.Consumer>
            {(context) => {
                if (context === undefined) {
                    throw new Error('AlertConsumer must be used within AlertProvider');
                }
                return children(context)
            }}
        </AlertContext.Consumer>
    )
}

// useAlert Hook
const useAlert = () => {
    const context = React.useContext(AlertContext);
    if(context === undefined)
        throw new Error('useAlert must be used within AlertProvider');
    return context;
}

// HOC to wrap components in AlertProvider
const withAlert = (Component) => (props) => (
    <AlertProvider>
        <Component {...props} />
    </AlertProvider>
);

export {
    AlertProvider,
    AlertConsumer,
    useAlert,
    withAlert
}